import ProfileAvatar from '@src/components/Molecules/ProfileAvatar';
import useDonationHelpers from '@src/hooks/useDonationHelper';
import { DonationHelper } from '@src/services/donationHelper';
import { devices } from '@src/styles/theme';
import { avatarInitial, avatarTint } from '@src/utils/chatAvatar';
import {
  formatBloodProduct,
  formatBloodType,
  formatDeadline,
} from '@src/utils/donationFormat';
import Image from 'next/image';
import React from 'react';
import styled from 'styled-components';

/**
 * 헌혈요청 상세 우측 사이드바 (Pple Blood Request 디자인 이식).
 *
 * 카드 3개: ① 요청자 프로필 + meta + 대화하기/돕겠습니다 ② 모집 현황 + 참여자 ③ 지정헌혈 안내
 * 값이 없는 항목(병원·지역·마감·필요수량)은 행/섹션을 그리지 않는다.
 * 응답률·본인인증 배지는 산출 근거가 없어 표시하지 않는다.
 */
type Props = {
  community: any;
  onChat?: () => void;
  chatDisabled?: boolean;
  /** 비로그인 등으로 참여가 불가할 때 호출(모달 안내) */
  onHelpBlocked?: () => void;
  canHelp?: boolean;
};

const STATUS_LABEL: Record<DonationHelper['status'], string> = {
  PLEDGED: '모집 중',
  RESERVED: '예약 확정',
  DONE: '완료',
  CANCELED: '취소',
};

const DonationDetailAside = ({
  community,
  onChat,
  chatDisabled,
  onHelpBlocked,
  canHelp = true,
}: Props) => {
  // 모집 현황/참여자 + '돕겠습니다' 참여 토글
  const { progress, isSubmitting, toggleHelp } = useDonationHelpers(
    community?.uuid
  );
  const iAmHelping = !!progress?.iAmHelping;

  const writer = community?.writer;
  const name = writer?.displayName ?? '';
  const tint = avatarTint(writer?.uuid || community?.uuid || 'w');
  const deadline = formatDeadline(community?.deadlineAt);

  const metas: Array<{ k: string; v: string }> = [];
  if (community?.hospitalName)
    metas.push({ k: '병원', v: community.hospitalName });
  if (community?.hospitalAddress)
    metas.push({ k: '주소', v: community.hospitalAddress });
  if (community?.region) metas.push({ k: '지역', v: community.region });
  if (deadline) metas.push({ k: '마감', v: deadline.label });
  const bloodType = formatBloodType(community?.patient?.bloodType);
  const bloodProduct = formatBloodProduct(community?.bloodProduct);
  if (bloodType || bloodProduct) {
    metas.push({
      k: '필요 혈액',
      v: [bloodType, bloodProduct].filter(Boolean).join(' · '),
    });
  }

  const required = progress?.requiredCount ?? community?.requiredCount ?? null;
  const fulfilled = progress?.fulfilledCount ?? 0;
  const pct =
    required && required > 0
      ? Math.min(100, Math.round((fulfilled / required) * 100))
      : 0;
  const helpers = progress?.helpers ?? [];

  return (
    <Wrap>
      {/* ① 요청자 프로필 */}
      <Card>
        <Head>
          <ProfileAvatar url={writer?.profileImageUrl} name={name} size={52} />
          <HeadInfo>
            <Name>{name || '요청자'}</Name>
            <SubText>수혈 요청자</SubText>
          </HeadInfo>
        </Head>

        {metas.length > 0 && (
          <MetaBox>
            {metas.map((m) => (
              <MetaRow key={m.k}>
                <MetaKey>{m.k}</MetaKey>
                <MetaValue>{m.v}</MetaValue>
              </MetaRow>
            ))}
          </MetaBox>
        )}

        <Actions>
          {onChat && (
            <ChatButton onClick={onChat} disabled={chatDisabled}>
              {chatDisabled ? '채팅방 여는 중…' : '대화하기'}
            </ChatButton>
          )}
          <HelpButton
            helping={iAmHelping}
            disabled={isSubmitting}
            onClick={() => {
              if (!canHelp) {
                onHelpBlocked?.();
                return;
              }
              toggleHelp();
            }}
          >
            {iAmHelping ? '✓ 돕기로 했어요' : '돕겠습니다'}
          </HelpButton>
          <HelpHint>
            &apos;돕겠습니다&apos;를 누르면 요청자에게 알림이 가고 모집 현황에
            표시돼요.
          </HelpHint>
        </Actions>
      </Card>

      {/* ② 모집 현황 — 필요 수량이 없어도 참여자가 있으면 보여준다 */}
      {(required != null || fulfilled > 0 || helpers.length > 0) && (
        <Card>
          <SectionTop>
            <SectionTitle>모집 현황</SectionTitle>
            <SectionCount>
              {required != null
                ? `${fulfilled} / ${required}팩`
                : `${fulfilled}명 참여`}
            </SectionCount>
          </SectionTop>
          <Track>
            <Fill style={{ width: `${pct}%` }} />
          </Track>
          {(helpers.length > 0 || required > fulfilled) && (
            <Helpers>
              {helpers.map((h) => {
                const ht = avatarTint(h.accountUuid || h.uuid);
                return (
                  <HelperRow key={h.uuid}>
                    <Avatar tint={ht.tint} ink={ht.ink} size={34}>
                      {avatarInitial(h.displayName)}
                    </Avatar>
                    <HelperInfo>
                      <HelperName>{h.displayName}</HelperName>
                      <HelperNote>
                        {h.visitAt
                          ? `${new Date(h.visitAt).getMonth() + 1}월 ${new Date(
                              h.visitAt
                            ).getDate()}일 ${
                              h.status === 'DONE' ? '헌혈 완료' : '방문 예정'
                            }`
                          : '방문 일정 조율 중'}
                      </HelperNote>
                    </HelperInfo>
                    <StatusBadge muted={h.status === 'PLEDGED'}>
                      {STATUS_LABEL[h.status]}
                    </StatusBadge>
                  </HelperRow>
                );
              })}
              {/* 아직 안 찬 자리 — 디자인의 점선 아바타 플레이스홀더 */}
              {Array.from({
                length: Math.max(
                  0,
                  required - Math.max(fulfilled, helpers.length)
                ),
              }).map((_, i) => (
                <HelperRow key={`slot-${i}`}>
                  <EmptyAvatar />
                  <HelperInfo>
                    <HelperName>한 분 더 필요해요</HelperName>
                    <HelperNote>
                      {[bloodType, bloodProduct].filter(Boolean).join(' ')} 1팩
                    </HelperNote>
                  </HelperInfo>
                </HelperRow>
              ))}
            </Helpers>
          )}
        </Card>
      )}

      {/* ③ 지정헌혈 안내 */}
      <GuideCard>
        <GuideTitle>지정헌혈이 처음이신가요?</GuideTitle>
        <GuideText>
          신분증만 있으면 됩니다. 병원 헌혈실에서 요청자 이름을 말하면 바로
          진행돼요.
        </GuideText>
        <GuideLink
          href="https://www.bloodinfo.net"
          target="_blank"
          rel="noopener noreferrer"
        >
          안내 자세히 보기 ›
        </GuideLink>
      </GuideCard>
    </Wrap>
  );
};

const Wrap = styled.aside`
  width: 328px;
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 24px;
  @media ${devices.tablet} {
    width: 100%;
    position: static;
  }
`;
const Card = styled.div`
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const Avatar = styled.div<{ tint: string; ink: string; size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  flex: none;
  border-radius: ${({ size }) => size / 2}px;
  background: ${({ tint }) => tint};
  color: ${({ ink }) => ink};
  font-size: ${({ size }) => (size >= 52 ? 18 : 13)}px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const HeadInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;
const Name = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #222222;
`;
const SubText = styled.span`
  font-size: 12.5px;
  color: #767676;
`;
const MetaBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #fbfbfb;
  border-radius: 14px;
`;
const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;
const MetaKey = styled.span`
  flex: none;
  font-size: 13px;
  color: #767676;
`;
const MetaValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #222222;
  text-align: right;
  word-break: keep-all;
`;
const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ChatButton = styled.button`
  height: 52px;
  border: none;
  border-radius: 26px;
  background: #ff4651;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #e03a44;
  }
  &:disabled {
    background: #ffa3a3;
    cursor: default;
  }
`;
/** 디자인의 참여 버튼 — 참여 중이면 채움, 아니면 연한 배경 */
const HelpButton = styled.button<{ helping: boolean }>`
  height: 52px;
  border-radius: 26px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #ff4651;
  background: ${({ helping }) => (helping ? '#FF4651' : '#FFF1ED')};
  color: ${({ helping }) => (helping ? '#ffffff' : '#FF4651')};
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
const HelpHint = styled.span`
  font-size: 11.5px;
  line-height: 18px;
  color: #b7b7b7;
  text-align: center;
`;
const SectionTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const SectionTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #222222;
`;
const SectionCount = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #ff4651;
`;
const Track = styled.div`
  height: 8px;
  border-radius: 4px;
  background: #f4f4f4;
  overflow: hidden;
`;
const Fill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: #ff4651;
  transition: width 0.3s;
`;
const Helpers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const HelperRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const HelperInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;
const HelperName = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: #222222;
`;
const HelperNote = styled.span`
  font-size: 12px;
  color: #b7b7b7;
`;
/**
 * 상태 뱃지 — 모집 중은 회색(아직 확정 전), 예약 확정·완료는 강조색(빨강).
 */
const StatusBadge = styled.span<{ muted: boolean }>`
  flex: none;
  height: 22px;
  padding: 0 9px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  white-space: nowrap;
  background: ${({ muted }) => (muted ? '#F4F4F4' : '#FFF1ED')};
  color: ${({ muted }) => (muted ? '#767676' : '#FF4651')};
`;
const GuideCard = styled.div`
  background: #fff1ed;
  border-radius: 20px;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const GuideTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #222222;
`;
const GuideLink = styled.a`
  font-size: 13px;
  font-weight: 700;
  color: #ff4651;
  cursor: pointer;
`;
const EmptyAvatar = styled.div`
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: 17px;
  border: 1px dashed #d8d8d8;
  background: #ffffff;
`;
const GuideText = styled.span`
  font-size: 13px;
  line-height: 21px;
  color: #767676;
`;

export default DonationDetailAside;
